import {
  Component,
  Directive,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SearchInputComponent } from '../form/search-input/search-input.component';
import {
  DropdownControlComponent,
  IDropdownSelect,
} from '../form/dropdown-control/dropdown-control.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { SidebarModule } from 'primeng/sidebar';
import { ShopFiltersComponent } from '../shop-filters/shop-filters.component';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, debounceTime, of, takeUntil } from 'rxjs';
import {
  Collection,
  SavedVehicle,
  SearchResultSortParameter,
} from '../../../graphql/generated/generated-types';
import {
  IMultiValueFilterParams,
  IShopParams,
} from '../../../routes/shop/shop.component';
import { CommonModule } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { Direction } from '../../utils/direction';
import { EditableManagedFilter } from '../../../routes/shop/managed-filter.class';

const sortByOptionList: IDropdownSelect[] = [
  {
    label: 'Newest',
    value: {},
  },
  {
    label: 'Price (low to high)',
    value: { sortName: 'price', sortValue: Direction.ASC },
  },
  {
    label: 'Price (high to low)',
    value: { sortName: 'price', sortValue: Direction.DESC },
  },
];

@Component({
  selector: 'app-shop-navigation-block',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    SearchInputComponent,
    RippleModule,
    DropdownControlComponent,
    PaginationComponent,
    SidebarModule,
    ShopFiltersComponent,
    MenuModule,
    SkeletonModule
  ],
  templateUrl: './shop-navigation-block.component.html',
  styleUrl: './shop-navigation-block.component.scss',
})
export class ShopNavigationBlockComponent
  implements OnInit, OnDestroy, OnChanges
{
  @ViewChild(ShopFiltersComponent, { static: false })
  shopFiltersComponent: ShopFiltersComponent;
  @Input() sort: SearchResultSortParameter;
  @Input() loading!: boolean;
  @Input() searchTerm!: string;

  @Input() perPage: number = 26;
  @Input() page$!: Observable<number>;

  @Input() collection!: Collection;
  @Input() leftFilters: EditableManagedFilter[];
  @Input() topFilters: EditableManagedFilter[];
  @Input() totalResults = 0;
  @Input() activeLeftFilters: IMultiValueFilterParams;
  @Input() activeQuickFilters: IMultiValueFilterParams;
  @Input() loading$: Observable<boolean> = of(false);
  @Input() initialLoading!: boolean;
 @Input() activeVehicle: SavedVehicle;
 
  public searchControl: FormControl = new FormControl('');
  public sortByControl: FormControl = new FormControl();
  public sortByOptions: IDropdownSelect[] = sortByOptionList;

  public filtersVisible: boolean = false;
  private unsubscribeTrigger$: Subject<void> = new Subject<void>();
  public sortMenuItems: MenuItem[] = [
    {
      label: 'Sort by',
      items: [
        {
          label: 'Newest',
          command: () => {
            this.clearSort();
            this.sortMenuItems[0].items![0].styleClass = 'selected-menu-item';
            this.sortMenuItems[0].items![1].styleClass = undefined;
            this.sortMenuItems[0].items![2].styleClass = undefined;
          },
        },
        {
          label: 'Price (low to high)',
          sortName: 'price',
          sortValue: Direction.ASC,
          styleClass: undefined,
          command: () => {
            this.sortByControl.setValue({
              sortName: 'price',
              sortValue: Direction.ASC,
            });
            this.sortMenuItems[0].items![0].styleClass = undefined;
            this.sortMenuItems[0].items![1].styleClass = 'selected-menu-item';
            this.sortMenuItems[0].items![2].styleClass = undefined;
          },
        },
        {
          label: 'Price (high to low)',
          sortName: 'price',
          sortValue: Direction.DESC,
          command: () => {
            this.sortByControl.setValue({
              sortName: 'price',
              sortValue: Direction.DESC,
            });
            this.sortMenuItems[0].items![0].styleClass = undefined;
            this.sortMenuItems[0].items![1].styleClass = undefined;
            this.sortMenuItems[0].items![2].styleClass = 'selected-menu-item';
          },
        },
      ],
    },
  ];

  public activeLeftFiltersCount: number = 0;
  public activeQuickFiltersCount: number = 0;

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit(): void {
    if (this.searchTerm) {
      this.searchControl.setValue(this.searchTerm, { emitEvent: false });
    }
    if (this.sort) {
      if (this.sort.price) {
        const key = Object.keys(this.sort)[0];
        this.sortByControl.setValue(
          { sortName: key, sortValue: this.sort.price },
          { emitEvent: false }
        );
        if (this.sort.price == (Direction.ASC as any)) {
          this.sortMenuItems[0].items![0].styleClass = undefined;
          this.sortMenuItems[0].items![1].styleClass = 'selected-menu-item';
          this.sortMenuItems[0].items![2].styleClass = undefined;
        } else {
          this.sortMenuItems[0].items![0].styleClass = undefined;
          this.sortMenuItems[0].items![1].styleClass = undefined;
          this.sortMenuItems[0].items![2].styleClass = 'selected-menu-item';
        }
      } else {
        this.sortMenuItems[0].items![0].styleClass = 'selected-menu-item';
        this.sortMenuItems[0].items![1].styleClass = undefined;
        this.sortMenuItems[0].items![2].styleClass = undefined;
      }
    }
    this.subscribeToSortValueChanges();
    this.subscribeToSearchControlValueChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribeTrigger$.next();
    this.unsubscribeTrigger$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(!this.initialLoading){
      if (changes['activeLeftFilters']) {
        this.activeLeftFiltersCount = Object.keys(changes['activeLeftFilters'].currentValue)?.length || 0;
      }
      if(changes['activeQuickFilters']) {
        this.activeQuickFiltersCount = Object.keys(changes['activeQuickFilters'].currentValue)?.length || 0;
      }
    }

  }

  public clearLeftFilters() {
    if (this.shopFiltersComponent) {
      this.shopFiltersComponent.clearFilters();
      this.closeFiltersSidebar();
    }
  }
  public clearSearchControl() {
    this.searchControl?.setValue('', { emitEvent: false });
  }

  private subscribeToSortValueChanges() {
    this.sortByControl.valueChanges
      .pipe(takeUntil(this.unsubscribeTrigger$))
      .subscribe({
        next: (value) => {
          let sortParam: string | null;

          if (value && value.sortName && value.sortValue) {
            sortParam = value.sortName + ':' + value.sortValue;
          } else {
            sortParam = null;
          }

          const queryParamsNew: IShopParams = {
            sort: sortParam,
          };
          this.updateQueryParams(queryParamsNew);
        },
      });
  }

  private subscribeToSearchControlValueChanges() {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.unsubscribeTrigger$), debounceTime(400))
      .subscribe({
        next: (value) => {
          const queryParamsNew: IShopParams = {
            search: value || null,
          };
          this.updateQueryParams(queryParamsNew);
        },
      });
  }

  private updateQueryParams(queryParamsNew: IShopParams): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParamsNew as IShopParams,
      queryParamsHandling: 'merge',
      state: {
        noScroll: true,
      },
    });
  }

  public clearSort() {
    const queryParamsNew: IShopParams = {
      sort: null,
    };
    this.updateQueryParams(queryParamsNew);
  }

  public closeFiltersSidebar() {
    this.filtersVisible = false;
  }
}
